<template>
    <div
        :id="id"
        ref="wrapperContainer"
        class="tl-input-phone-number"
    >
        <InputText
            :model-value="modelState"
            :label="label"
            :error="isPhoneValid ? error : t('input-phone-number.invalid')"
            :focus="focus"
            :disabled="disabled"
            :placeholder="placeholder"
            :options="{ type: 'tel' }"
            :test-id="testId"
            @mounted="initTelInput"
            @update:model-value="handleUpdate"
        />
        <div
            ref="dropdownContainer"
            class="dropdown-container"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, watch, unref, onBeforeUnmount, shallowRef, nextTick } from 'vue'
import { useI18n } from '../../utilities/i18n'
import intlTelInput from 'intl-tel-input'
import { lazyBlockToolsInjectionKey, repeatableContainerIdKey } from 'widget-components/integrations/CustomPage/injection-keys'
import type { BlockPath, InputPhoneNumber, LazyBlockToolsInject } from 'widget-components/integrations/CustomPage/types'
import { isEmpty } from 'ramda'
import equals from 'fast-deep-equal/es6'
import { sentenceCase } from 'change-case'
import InputText from '../InputText/InputText.vue'
import { CODE_TO_COUNTRY_EXCEPTIONS, LOCALE_TO_COUNTRY_EXCEPTIONS } from './inputPhoneNumber.utils'
import type { Iso2 } from 'intl-tel-input/data'
import { useResizeObserver } from '@vueuse/core'

import 'intl-tel-input/build/css/intlTelInput.css'

const { t, locale } = useI18n()

const localeCountry = computed(() => {
    return LOCALE_TO_COUNTRY_EXCEPTIONS[unref(locale)] || unref(locale)
})

type ModelValueObj = { countryCode: string, number: string, fullNumber: string }

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
    id?: string
    modelValue: string | ModelValueObj
    modelValueFormat?: 'string' | 'object'
    initialValue?: string | ModelValueObj
    label?: string
    disabled?: boolean
    focus?: boolean
    error?: string | boolean
    blockPath?: BlockPath
    placeholder?: string
    testId?: string
    separateDialCode?: boolean
}>(), {
    modelValueFormat: 'string',
    separateDialCode: false,
})

const emit = defineEmits(['update:modelValue', 'update:details', 'submit'])

const {
    requestBlockConfig,
    getLazyBlockProp,
} = inject(lazyBlockToolsInjectionKey, {
    requestBlockConfig: () => {},
    getLazyBlockProp: () => {} ,
    dispatchComponentAction: async () => {},
} as LazyBlockToolsInject)

const repeatableContainerId = inject(repeatableContainerIdKey, null)

const input = shallowRef<HTMLInputElement | null>(null)
const telInput = ref<ReturnType<typeof intlTelInput> | null>(null)
const wrapperContainer = ref<HTMLElement | null>(null)
const dropdownContainer = ref<HTMLElement>()
const seperateDialCodeWidth = ref<string>()

const modelValueString = computed(() => {
    return props.modelValueFormat === 'string'
})

const modelState = ref<string>('')

const dropdownOpened = ref(false)
const label = computed(() => props.label)

const isPhoneValid = ref(true)

const getPhoneDetails = (val?: string) => {
    if (!telInput.value) return {}
    const fullNumber = val ?? telInput.value.getNumber()
    const countryDetails = telInput.value.getSelectedCountryData()
    const prefix = countryDetails.dialCode ? `+${countryDetails.dialCode}` : ''
    const countryName = countryDetails.name
    const countryCode = countryDetails.iso2
    const phoneNumber = fullNumber
        .replace(prefix, '')
        .replace('+', '')
    const isValid = fullNumber.length
        ? telInput.value.isValidNumber()
        : true

    return {
        phoneNumber,
        fullNumber,
        prefix,
        countryName,
        countryCode,
        isValid,
    }
}

const updateDialCodePadding = async () => {
    if (!props.separateDialCode || !wrapperContainer.value) return
    if (wrapperContainer.value.offsetParent === null) return
    await nextTick()
    const countryContainer = wrapperContainer.value.querySelector<HTMLElement>('.iti__country-container')
    if (!countryContainer) return
    const width = countryContainer.offsetWidth + 5
    seperateDialCodeWidth.value = `${width}px`
}

const removeUnknownTooltip = () => setTimeout(() => {
    const elem = wrapperContainer.value?.querySelector('.iti__selected-country')
    if (!elem) {
        return
    }
    const title = elem.getAttribute('title')

    if (title === 'Unknown') {
        elem.removeAttribute('title')
    }
}, 1000)

const handleUpdate = (val?: string) => {
    const details = getPhoneDetails(val)

    if (isEmpty(details) && !telInput.value) return

    const { fullNumber, phoneNumber, isValid: valid } = details

    isPhoneValid.value = !!valid

    if (modelValueString.value) {
        emit('update:modelValue', fullNumber)
    } else {
        const value = {
            countryCode: details.prefix,
            number: details.phoneNumber,
            fullNumber: details.fullNumber,
        }
        emit('update:modelValue', value)
    }

    if (typeof fullNumber === 'string') {
        modelState.value = props.separateDialCode ? phoneNumber : fullNumber
    }

    emit('update:details', details)
}

const handleEscKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
        event.stopPropagation()
        const instance = intlTelInput.getInstance(input.value!)
        if (instance) {
            // @ts-expect-error private _closeDropdown; (not exposed)
            instance._closeDropdown()
        }
    }
}

const initTelInput = async (inputElem: HTMLInputElement) => {
    if (!dropdownContainer.value) return

    const onlyCountries: string[] = []
    const localizedCountries: Record<string, string> = {}
    let initialCountry: string | undefined

    if (props.id) {
        const componentIndex = props.blockPath?.at(-1)
        const cacheKey = (repeatableContainerId && componentIndex !== undefined)
            ? `${repeatableContainerId}_${componentIndex}`
            : undefined

        await requestBlockConfig({ id: props.id, type: 'INPUT_PHONE_NUMBER', cacheKey })
        const countries = getLazyBlockProp<InputPhoneNumber['selectedItems'] | undefined>(props.id, 'items')
        if (countries) {
            for (const country of countries) {
                if (country.selected) {
                    initialCountry = country.country
                }
                const countryCodeIso = country.country.toLowerCase()
                onlyCountries.push(countryCodeIso)
                localizedCountries[countryCodeIso] = country.label || sentenceCase(country.country)
            }
            if (!initialCountry && typeof props.initialValue === 'object') {
                const countryCode = Number((props.initialValue as ModelValueObj).countryCode)
                const exceptionalCountry = CODE_TO_COUNTRY_EXCEPTIONS[countryCode]

                initialCountry = (exceptionalCountry ? countries.find(c => c.country === exceptionalCountry)?.country : undefined)
                                 ?? countries.find(c => c.countryCode === countryCode)?.country
            }
        }
    }

    input.value = inputElem

    telInput.value = intlTelInput(inputElem, {
        dropdownContainer: dropdownContainer.value,
        loadUtils: () => import('intl-tel-input/utils'),
        onlyCountries: onlyCountries as Iso2[],
        separateDialCode: props.separateDialCode,
        ...initialCountry
            ? { initialCountry: initialCountry as Iso2 }
            : {
                initialCountry: 'auto',
                geoIpLookup: callback => fetch(window.location.hostname === 'localhost'
                    ? 'https://ipinfo.io/json'
                    : 'https://ipinfo.io/json?token=7817447db72b7e',
                )
                    .then(res => {
                        if (res.ok) {
                            return res.json()
                                .then(data => callback(data.country))
                                .catch(() => callback(localeCountry.value))
                        } else {
                            callback(localeCountry.value)
                        }
                    }),
            },
        i18n: {
            searchPlaceholder: t('search'),
            ...localizedCountries,
        },
    })

    if (!initialCountry && typeof props.initialValue === 'string') {
        telInput.value.setNumber(props.initialValue as string)
    }

    input.value.addEventListener('countrychange', async (event) => {
        const val = (event.target as HTMLInputElement).value
        handleUpdate(val)
        await updateDialCodePadding()
    })

    input.value.addEventListener('open:countrydropdown', () => {
        document.addEventListener('keydown', handleEscKey, { capture: true })

        const searchInput = document.querySelector<HTMLInputElement>('.iti__search-input')
        if (searchInput) {
            searchInput.value = ''
            searchInput.dispatchEvent(new Event('input'))
        }

        dropdownOpened.value = true
        input.value?.scrollIntoView({
            block: 'nearest',
        })
    })

    input.value.addEventListener('close:countrydropdown', () => {
        document.removeEventListener('keydown', handleEscKey, { capture: true })

        dropdownOpened.value = false
    })

    await telInput.value.promise
    await updateDialCodePadding()

    handleUpdate()
}

onMounted(() => {
    if (modelValueString.value) {
        modelState.value = props.initialValue as string
    } else {
        modelState.value = (props.initialValue as ModelValueObj | undefined)?.number || ''
    }

    removeUnknownTooltip()
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleEscKey, { capture: true })
})

useResizeObserver(computed(() => props.separateDialCode ? wrapperContainer.value : null), () => {
    updateDialCodePadding()
})

watch(() => props.modelValue, () => {
    const val = modelValueString.value ? props.modelValue as string : (props.modelValue as ModelValueObj | undefined)?.number
    if (!equals(val, modelState.value) && telInput.value) {
        handleUpdate(val)
    }
})
</script>

<style scoped lang="sass">
.tl-input-phone-number
    --iti-country-container-width: v-bind(seperateDialCodeWidth)

@import './InputPhoneNumber.sass'
</style>
